import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePlayerStore } from '../../stores/usePlayerStore'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'

interface Material {
  id: string
  name: string
  category: string
  unit: string
  price_per_unit: number
  is_available: boolean
}

interface Machinery {
  id: string
  name: string
  category: string
  capacity: number
  efficiency: number
  price: number
  is_available: boolean
}

interface RequiredMaterial {
  material_id?: string
  material_name: string
  quantity: number
  unit: string
  is_new: boolean
}

interface RequiredMachinery {
  machinery_id?: string
  machinery_name: string
  quantity: number
  is_new: boolean
}

interface ProductRequest {
  id: string
  product_name: string
  description?: string
  category: string
  target_price?: number
  required_materials: RequiredMaterial[]
  required_machinery: RequiredMachinery[]
  production_process: string
  estimated_production_time: number
  status: 'pending' | 'under_review' | 'approved' | 'rejected'
  created_at: string
  reviewed_at?: string
  admin_notes?: string
}

export const ProductRequestManager: React.FC = () => {
  const { t } = useTranslation()
  const { player } = usePlayerStore()
  const [requests, setRequests] = useState<ProductRequest[]>([])
  const [materials, setMaterials] = useState<Material[]>([])
  const [machinery, setMachinery] = useState<Machinery[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showRequestForm, setShowRequestForm] = useState(false)
  const [newRequest, setNewRequest] = useState({
    product_name: '',
    description: '',
    category: '',
    target_price: '',
    production_process: '',
    estimated_production_time: ''
  })
  const [requiredMaterials, setRequiredMaterials] = useState<RequiredMaterial[]>([])
  const [requiredMachinery, setRequiredMachinery] = useState<RequiredMachinery[]>([])
  const [newMaterial, setNewMaterial] = useState({
    material_id: '',
    material_name: '',
    quantity: '',
    unit: '',
    is_new: false
  })
  const [newMachineryItem, setNewMachineryItem] = useState({
    machinery_id: '',
    machinery_name: '',
    quantity: '',
    is_new: false
  })

  const categories = [
    { value: 'food', label: t('productRequests.food') },
    { value: 'electronics', label: t('productRequests.electronics') },
    { value: 'materials', label: t('productRequests.materials') },
    { value: 'textiles', label: t('productRequests.textiles') },
    { value: 'chemicals', label: t('productRequests.chemicals') },
    { value: 'construction', label: t('productRequests.construction') }
  ]

  useEffect(() => {
    if (player?.id) {
      loadRequests()
      loadMaterials()
      loadMachinery()
    }
  }, [player?.id])

  const loadRequests = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('https://business-tycoon-worker.smnshzh.workers.dev/api/product-requests')
      const data = await response.json()
      
      if (data.success) {
        setRequests(data.data || [])
      } else {
        console.error('Failed to load requests:', data.error)
        // Fallback to mock data for demo
        const mockRequests: ProductRequest[] = [
          {
            id: 'req_1',
            product_name: 'نان بربری مخصوص',
            description: 'نان بربری با کیفیت بالا و طعم خاص',
            category: 'food',
            target_price: 150,
            required_materials: [
              { material_id: 'mat_1', material_name: 'آرد گندم کامل', quantity: 2, unit: 'کیلوگرم', is_new: false },
              { material_name: 'مخمر مخصوص', quantity: 50, unit: 'گرم', is_new: true }
            ],
            required_machinery: [
              { machinery_id: 'mach_1', machinery_name: 'دستگاه مخلوط کن', quantity: 1, is_new: false },
              { machinery_name: 'فر پخت مخصوص', quantity: 1, is_new: true }
            ],
            production_process: 'مخلوط کردن مواد، تخمیر، پخت، بسته‌بندی',
            estimated_production_time: 120,
            status: 'approved',
            created_at: new Date().toISOString(),
            reviewed_at: new Date().toISOString(),
            admin_notes: 'درخواست تایید شد. محصول قابل تولید است.'
          }
        ]
        setRequests(mockRequests)
      }
    } catch (error) {
      console.error('Error loading requests:', error)
      // Fallback to mock data for demo
      const mockRequests: ProductRequest[] = [
        {
          id: 'req_1',
          product_name: 'نان بربری مخصوص',
          description: 'نان بربری با کیفیت بالا و طعم خاص',
          category: 'food',
          target_price: 150,
          required_materials: [
            { material_id: 'mat_1', material_name: 'آرد گندم کامل', quantity: 2, unit: 'کیلوگرم', is_new: false },
            { material_name: 'مخمر مخصوص', quantity: 50, unit: 'گرم', is_new: true }
          ],
          required_machinery: [
            { machinery_id: 'mach_1', machinery_name: 'دستگاه مخلوط کن', quantity: 1, is_new: false },
            { machinery_name: 'فر پخت مخصوص', quantity: 1, is_new: true }
          ],
          production_process: 'مخلوط کردن مواد، تخمیر، پخت، بسته‌بندی',
          estimated_production_time: 120,
          status: 'approved',
          created_at: new Date().toISOString(),
          reviewed_at: new Date().toISOString(),
          admin_notes: 'درخواست تایید شد. محصول قابل تولید است.'
        }
      ]
      setRequests(mockRequests)
    } finally {
      setIsLoading(false)
    }
  }

  const loadMaterials = async () => {
    try {
      // Mock data for materials
      const mockMaterials: Material[] = [
        { id: 'mat_1', name: 'آرد گندم', category: 'food', unit: 'کیلوگرم', price_per_unit: 5, is_available: true },
        { id: 'mat_2', name: 'شیر تازه', category: 'food', unit: 'لیتر', price_per_unit: 3, is_available: true },
        { id: 'mat_3', name: 'تخم مرغ', category: 'food', unit: 'عدد', price_per_unit: 0.5, is_available: true },
        { id: 'mat_4', name: 'نمک', category: 'food', unit: 'کیلوگرم', price_per_unit: 2, is_available: true },
        { id: 'mat_5', name: 'شکر', category: 'food', unit: 'کیلوگرم', price_per_unit: 4, is_available: true },
        { id: 'mat_6', name: 'روغن', category: 'food', unit: 'لیتر', price_per_unit: 8, is_available: true }
      ]
      setMaterials(mockMaterials)
    } catch (error) {
      console.error('Error loading materials:', error)
    }
  }

  const loadMachinery = async () => {
    try {
      // Mock data for machinery
      const mockMachinery: Machinery[] = [
        { id: 'mach_1', name: 'دستگاه مخلوط کن', category: 'food', capacity: 50, efficiency: 0.9, price: 5000, is_available: true },
        { id: 'mach_2', name: 'فر پخت نان', category: 'food', capacity: 20, efficiency: 0.95, price: 8000, is_available: true },
        { id: 'mach_3', name: 'دستگاه بسته‌بندی', category: 'packaging', capacity: 100, efficiency: 0.85, price: 3000, is_available: true },
        { id: 'mach_4', name: 'دستگاه برش', category: 'food', capacity: 30, efficiency: 0.9, price: 4000, is_available: true },
        { id: 'mach_5', name: 'دستگاه خنک‌کننده', category: 'food', capacity: 40, efficiency: 0.8, price: 6000, is_available: true }
      ]
      setMachinery(mockMachinery)
    } catch (error) {
      console.error('Error loading machinery:', error)
    }
  }

  const handleAddMaterial = () => {
    if (!newMaterial.material_name || !newMaterial.quantity) {
      alert(t('productRequests.fillRequiredFields'))
      return
    }

    const material: RequiredMaterial = {
      material_id: newMaterial.material_id || undefined,
      material_name: newMaterial.material_name,
      quantity: parseInt(newMaterial.quantity),
      unit: newMaterial.unit,
      is_new: newMaterial.is_new
    }

    setRequiredMaterials([...requiredMaterials, material])
    setNewMaterial({
      material_id: '',
      material_name: '',
      quantity: '',
      unit: '',
      is_new: false
    })
  }

  const handleAddMachinery = () => {
    if (!newMachineryItem.machinery_name || !newMachineryItem.quantity) {
      alert(t('productRequests.fillRequiredFields'))
      return
    }

    const machineryItem: RequiredMachinery = {
      machinery_id: newMachineryItem.machinery_id || undefined,
      machinery_name: newMachineryItem.machinery_name,
      quantity: parseInt(newMachineryItem.quantity),
      is_new: newMachineryItem.is_new
    }

    setRequiredMachinery([...requiredMachinery, machineryItem])
    setNewMachineryItem({
      machinery_id: '',
      machinery_name: '',
      quantity: '',
      is_new: false
    })
  }

  const handleRemoveMaterial = (index: number) => {
    setRequiredMaterials(requiredMaterials.filter((_, i) => i !== index))
  }

  const handleRemoveMachinery = (index: number) => {
    setRequiredMachinery(requiredMachinery.filter((_, i) => i !== index))
  }

  const handleSubmitRequest = async () => {
    if (!player?.id || !newRequest.product_name || !newRequest.category) {
      alert(t('productRequests.fillRequiredFields'))
      return
    }

    if (requiredMaterials.length === 0) {
      alert(t('productRequests.addAtLeastOneMaterial'))
      return
    }

    if (requiredMachinery.length === 0) {
      alert(t('productRequests.addAtLeastOneMachinery'))
      return
    }

    setIsLoading(true)
    try {
      const requestData = {
        ...newRequest,
        player_id: player.id,
        target_price: parseFloat(newRequest.target_price) || 0,
        estimated_production_time: parseInt(newRequest.estimated_production_time) || 0,
        required_materials: requiredMaterials,
        required_machinery: requiredMachinery
      }

      const response = await fetch('https://business-tycoon-worker.smnshzh.workers.dev/api/product-requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      })

      const data = await response.json()
      
      if (data.success) {
        setRequests([...requests, data.data])
        setShowRequestForm(false)
        setNewRequest({
          product_name: '',
          description: '',
          category: '',
          target_price: '',
          production_process: '',
          estimated_production_time: ''
        })
        setRequiredMaterials([])
        setRequiredMachinery([])
        alert(t('productRequests.requestSubmitted'))
      } else {
        alert(t('productRequests.submitError') + ': ' + data.error)
      }
    } catch (error) {
      console.error('Error submitting request:', error)
      alert(t('productRequests.submitError'))
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'under_review':
        return 'bg-blue-100 text-blue-800'
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return t('productRequests.pending')
      case 'under_review':
        return t('productRequests.underReview')
      case 'approved':
        return t('productRequests.approved')
      case 'rejected':
        return t('productRequests.rejected')
      default:
        return status
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fa-IR')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card title={`📝 ${t('productRequests.title')}`} subtitle={t('productRequests.subtitle')}>
        <div className="flex justify-between items-center">
          <div>
            <p className="text-gray-600">
              {t('productRequests.totalRequests')}: {requests.length}
            </p>
            <p className="text-sm text-gray-500">
              {t('productRequests.approvedRequests')}: {requests.filter(r => r.status === 'approved').length}
            </p>
          </div>
          <Button
            onClick={() => setShowRequestForm(true)}
            variant="primary"
          >
            {t('productRequests.newRequest')}
          </Button>
        </div>
      </Card>

      {/* Request Form Modal */}
      {showRequestForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <Card title={t('productRequests.newRequest')} subtitle={t('productRequests.newRequestSubtitle')} className="w-full max-w-4xl">
            <div className="space-y-6">
              {/* Basic Product Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('productRequests.productName')} *
                  </label>
                  <input
                    type="text"
                    value={newRequest.product_name}
                    onChange={(e) => setNewRequest({ ...newRequest, product_name: e.target.value })}
                    className="input-field"
                    placeholder={t('productRequests.productNamePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('productRequests.category')} *
                  </label>
                  <select
                    value={newRequest.category}
                    onChange={(e) => setNewRequest({ ...newRequest, category: e.target.value })}
                    className="input-field"
                  >
                    <option value="">{t('productRequests.selectCategory')}</option>
                    {categories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('productRequests.targetPrice')}
                  </label>
                  <input
                    type="number"
                    value={newRequest.target_price}
                    onChange={(e) => setNewRequest({ ...newRequest, target_price: e.target.value })}
                    className="input-field"
                    placeholder={t('productRequests.targetPricePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('productRequests.estimatedProductionTime')} (دقیقه)
                  </label>
                  <input
                    type="number"
                    value={newRequest.estimated_production_time}
                    onChange={(e) => setNewRequest({ ...newRequest, estimated_production_time: e.target.value })}
                    className="input-field"
                    placeholder={t('productRequests.estimatedProductionTimePlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('productRequests.description')}
                </label>
                <textarea
                  value={newRequest.description}
                  onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                  className="input-field"
                  rows={3}
                  placeholder={t('productRequests.descriptionPlaceholder')}
                ></textarea>
              </div>

              {/* Required Materials Section */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {t('productRequests.requiredMaterials')} *
                </h3>
                
                {/* Add Material Form */}
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('productRequests.selectExistingMaterial')}
                      </label>
                      <select
                        value={newMaterial.material_id}
                        onChange={(e) => {
                          const material = materials.find(m => m.id === e.target.value)
                          setNewMaterial({
                            ...newMaterial,
                            material_id: e.target.value,
                            material_name: material?.name || '',
                            unit: material?.unit || '',
                            is_new: false
                          })
                        }}
                        className="input-field"
                      >
                        <option value="">{t('productRequests.selectMaterial')}</option>
                        {materials.map((material) => (
                          <option key={material.id} value={material.id}>
                            {material.name} ({material.unit})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('productRequests.materialName')}
                      </label>
                      <input
                        type="text"
                        value={newMaterial.material_name}
                        onChange={(e) => setNewMaterial({ ...newMaterial, material_name: e.target.value, is_new: true })}
                        className="input-field"
                        placeholder={t('productRequests.materialNamePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('productRequests.quantity')}
                      </label>
                      <input
                        type="number"
                        value={newMaterial.quantity}
                        onChange={(e) => setNewMaterial({ ...newMaterial, quantity: e.target.value })}
                        className="input-field"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('productRequests.unit')}
                      </label>
                      <input
                        type="text"
                        value={newMaterial.unit}
                        onChange={(e) => setNewMaterial({ ...newMaterial, unit: e.target.value })}
                        className="input-field"
                        placeholder={t('productRequests.unitPlaceholder')}
                      />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={handleAddMaterial} variant="primary" size="sm">
                        {t('productRequests.addMaterial')}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Materials List */}
                {requiredMaterials.length > 0 && (
                  <div className="space-y-2">
                    {requiredMaterials.map((material, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                        <div className="flex items-center space-x-4 space-x-reverse">
                          <span className="text-sm font-medium">{material.material_name}</span>
                          <span className="text-sm text-gray-600">{material.quantity} {material.unit}</span>
                          {material.is_new && (
                            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                              {t('productRequests.new')}
                            </span>
                          )}
                        </div>
                        <Button
                          onClick={() => handleRemoveMaterial(index)}
                          variant="secondary"
                          size="sm"
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Required Machinery Section */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {t('productRequests.requiredMachinery')} *
                </h3>
                
                {/* Add Machinery Form */}
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('productRequests.selectExistingMachinery')}
                      </label>
                      <select
                        value={newMachineryItem.machinery_id}
                        onChange={(e) => {
                          const machineryItem = machinery.find(m => m.id === e.target.value)
                          setNewMachineryItem({
                            ...newMachineryItem,
                            machinery_id: e.target.value,
                            machinery_name: machineryItem?.name || '',
                            is_new: false
                          })
                        }}
                        className="input-field"
                      >
                        <option value="">{t('productRequests.selectMachinery')}</option>
                        {machinery.map((mach) => (
                          <option key={mach.id} value={mach.id}>
                            {mach.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('productRequests.machineryName')}
                      </label>
                      <input
                        type="text"
                        value={newMachineryItem.machinery_name}
                        onChange={(e) => setNewMachineryItem({ ...newMachineryItem, machinery_name: e.target.value, is_new: true })}
                        className="input-field"
                        placeholder={t('productRequests.machineryNamePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('productRequests.quantity')}
                      </label>
                      <input
                        type="number"
                        value={newMachineryItem.quantity}
                        onChange={(e) => setNewMachineryItem({ ...newMachineryItem, quantity: e.target.value })}
                        className="input-field"
                        placeholder="0"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={handleAddMachinery} variant="primary" size="sm">
                        {t('productRequests.addMachinery')}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Machinery List */}
                {requiredMachinery.length > 0 && (
                  <div className="space-y-2">
                    {requiredMachinery.map((machineryItem, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                        <div className="flex items-center space-x-4 space-x-reverse">
                          <span className="text-sm font-medium">{machineryItem.machinery_name}</span>
                          <span className="text-sm text-gray-600">{t('productRequests.quantity')}: {machineryItem.quantity}</span>
                          {machineryItem.is_new && (
                            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                              {t('productRequests.new')}
                            </span>
                          )}
                        </div>
                        <Button
                          onClick={() => handleRemoveMachinery(index)}
                          variant="secondary"
                          size="sm"
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('productRequests.productionProcess')} *
                </label>
                <textarea
                  value={newRequest.production_process}
                  onChange={(e) => setNewRequest({ ...newRequest, production_process: e.target.value })}
                  className="input-field"
                  rows={4}
                  placeholder={t('productRequests.productionProcessPlaceholder')}
                ></textarea>
              </div>

              <div className="flex justify-end space-x-3 space-x-reverse">
                <Button variant="secondary" onClick={() => setShowRequestForm(false)}>
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={handleSubmitRequest} disabled={isLoading}>
                  {isLoading ? t('common.loading') : t('productRequests.submitRequest')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Requests List */}
      <Card title={t('productRequests.yourRequests')} subtitle={t('productRequests.yourRequestsSubtitle')}>
        {isLoading ? (
          <div className="flex justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : requests.length > 0 ? (
          <div className="space-y-4">
            {requests.map((request) => (
              <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-800">{request.product_name}</h3>
                    <p className="text-sm text-gray-600">{request.description}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                    {getStatusText(request.status)}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">{t('productRequests.category')}</h4>
                    <p className="text-sm text-gray-600">{categories.find(c => c.value === request.category)?.label}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">{t('productRequests.targetPrice')}</h4>
                    <p className="text-sm text-gray-600">{request.target_price?.toLocaleString()} {t('common.price')}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">{t('productRequests.requiredMaterials')}</h4>
                    <div className="space-y-1">
                      {request.required_materials.map((material, index) => (
                        <div key={index} className="text-sm text-gray-600">
                          • {material.material_name} ({material.quantity} {material.unit})
                          {material.is_new && <span className="text-blue-600 mr-2">({t('productRequests.new')})</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">{t('productRequests.requiredMachinery')}</h4>
                    <div className="space-y-1">
                      {request.required_machinery.map((machineryItem, index) => (
                        <div key={index} className="text-sm text-gray-600">
                          • {machineryItem.machinery_name} ({machineryItem.quantity})
                          {machineryItem.is_new && <span className="text-blue-600 mr-2">({t('productRequests.new')})</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <h4 className="font-medium text-gray-700 mb-1">{t('productRequests.productionProcess')}</h4>
                  <p className="text-sm text-gray-600">{request.production_process}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">{t('productRequests.estimatedProductionTime')}</h4>
                    <p className="text-sm text-gray-600">{request.estimated_production_time} {t('productRequests.minutes')}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">{t('productRequests.createdAt')}</h4>
                    <p className="text-sm text-gray-600">{formatDate(request.created_at)}</p>
                  </div>
                </div>

                {request.admin_notes && (
                  <div className="mb-3">
                    <h4 className="font-medium text-gray-700 mb-1">{t('productRequests.adminNotes')}</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{request.admin_notes}</p>
                  </div>
                )}

                {request.reviewed_at && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">{t('productRequests.reviewedAt')}</h4>
                    <p className="text-sm text-gray-600">{formatDate(request.reviewed_at)}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📝</span>
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              {t('productRequests.noRequests')}
            </h3>
            <p className="text-gray-500 mb-4">
              {t('productRequests.createFirstRequest')}
            </p>
            <Button variant="primary" onClick={() => setShowRequestForm(true)}>
              {t('productRequests.newRequest')}
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}